import React, { useState, useEffect } from 'react';
import Header from './Header';
import HeaderMobile from './HeaderMobile';
import Sidebar from './Sidebar';
import Footer from './Footer';
import { BASE_URL } from "./Constants.js";

const EmpList = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkboxStates, setCheckboxStates] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArea, setSelectedArea] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  const usersPerPage = 20;

  const handleToggleSidebar = () => {
    if (window.innerWidth >= 992) {
      setIsSidebarOpen(prev => !prev);
    }
  };

  useEffect(() => {
    const fetchEmpList = async () => {
      const token = localStorage.getItem('authToken');

      if (!token) {
        setError('No token found!');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${BASE_URL}emp_list`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        if (data.status && Array.isArray(data.data)) {
          setUsers(data.data);

          // Prepopulate checkboxStates from API
          const checkboxMap = {};
          data.data.forEach((emp) => {
            checkboxMap[emp.emp_code] = {
              is_uniform_access_given: emp.is_uniform_access_given === 'Y', // sridhar
              super_admin_role: emp.super_admin_role === 'Y', // ram sir
              employee_role: emp.employee_role === 'Y', // faiz
              admin_role: emp.admin_role === 'Y', // faiz
              branch_manager_role: emp.branch_manager_role === 'Y', // faiz

            };
          });
          setCheckboxStates(checkboxMap);

        } else {
          setError('Invalid response format');
        }
      } catch (err) {
        setError('Failed to fetch employee list');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchEmpList();
  }, []);

  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('No token found!');
          setLoading(false);
          return;
        }

        const response = await fetch(`${BASE_URL}areas`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch areas');
        }

        const data = await response.json();
        if (data && data.areas && Array.isArray(data.areas)) {
          const activeAreas = data.areas.filter(area => area.status === 'A');
          setAreas(activeAreas);

        } else {
          setError('No areas found');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAreas();
  }, []);

  const handleCheckboxChange = async (emp_code, field, isChecked) => {
    const updatedState = {
      ...checkboxStates[emp_code],
      [field]: isChecked,
    };

    setCheckboxStates(prev => ({
      ...prev,
      [emp_code]: updatedState,
    }));

    const token = localStorage.getItem('authToken');
    if (!token) {
      alert('Token not found');
      return;
    }

    const payload = {
      emp_code: emp_code,
      is_uniform_access_given: updatedState.is_uniform_access_given ? 'Y' : 'N',
      super_admin_role: updatedState.super_admin_role ? 'Y' : 'N',
      employee_role: updatedState.employee_role ? 'Y' : 'N',
      admin_role: updatedState.admin_role ? 'Y' : 'N',
      branch_manager_role: updatedState.branch_manager_role ? 'Y' : 'N',
    };

    try {
      const response = await fetch(`${BASE_URL}update_role`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!result.status) {
        alert('Failed to update role');
      }
    } catch (err) {
      console.error('Error updating role:', err);
      alert('Error updating role');
    }
  };

  const filteredUsers = users.filter((emp) => {
    const term = searchTerm.toLowerCase();
    return (
      emp.comp_name?.toLowerCase().includes(term) ||
      emp.mail_id?.toLowerCase().includes(term) ||
      emp.mobile?.toLowerCase().includes(term) ||
      emp.emp_code?.toLowerCase().includes(term)
    );
  });

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="page-wrapper">
      <HeaderMobile />
      <Sidebar isOpen={isSidebarOpen} />
      <div
        className="page-container"
        style={{
          marginLeft: isSidebarOpen && window.innerWidth >= 992 ? '250px' : '0',
          transition: 'margin-left 0.3s ease',
        }}
      >
        <Header onToggleSidebar={handleToggleSidebar} isSidebarOpen={isSidebarOpen} />
        <div className="main-content">
          <div className="section__content section__content--p30">
            <div className="container-fluid">
              <h2 className="mb-4">Employee List</h2>

              <div className="row mb-3">
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search by Name, Email, Mobile, or Emp Code"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>

              {loading ? (
                <div>Loading...</div>
              ) : error ? (
                <div style={{ color: 'red' }}>Error: {error}</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered table-striped">
                    <thead className="thead-dark">
                      <tr>
                        <th>Emp Code</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Department</th>
                        <th>Location</th>
                        <th>Candidate</th>
                        <th>Super Admin</th>
                        <th>Employee</th>
                        <th>Admin</th>
                        <th>Branch Manager</th>
                        <th>Areas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentUsers.map((emp, index) => (
                        <tr key={index}>
                          <td>{emp.emp_code}</td>
                          <td>{emp.comp_name}</td>
                          <td>{emp.mail_id}</td>
                          <td>{emp.mobile || 'N/A'}</td>
                          <td>{emp.dept_name}</td>
                          <td>{emp.location_name}</td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={checkboxStates[emp.emp_code]?.is_uniform_access_given || false}
                              onChange={(e) =>
                                handleCheckboxChange(emp.emp_code, 'is_uniform_access_given', e.target.checked)
                              }
                            />
                          </td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={checkboxStates[emp.emp_code]?.super_admin_role || false}
                              onChange={(e) =>
                                handleCheckboxChange(emp.emp_code, 'super_admin_role', e.target.checked)
                              }
                            />
                          </td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={checkboxStates[emp.emp_code]?.employee_role || false}
                              onChange={(e) =>
                                handleCheckboxChange(emp.emp_code, 'employee_role', e.target.checked)
                              }
                            />
                          </td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={checkboxStates[emp.emp_code]?.admin_role || false}
                              onChange={(e) =>
                                handleCheckboxChange(emp.emp_code, 'admin_role', e.target.checked)
                              }
                            />
                          </td>
                          <td className="text-center">
                            <input
                              type="checkbox"
                              checked={checkboxStates[emp.emp_code]?.branch_manager_role || false}
                              onChange={(e) =>
                                handleCheckboxChange(emp.emp_code, 'branch_manager_role', e.target.checked)
                              }
                            />
                          </td>


                          <td>
                            <select
                              className="form-control"
                              value={selectedArea} // controlled component
                              onChange={(e) => setSelectedArea(e.target.value)}
                            >
                              <option value="">Select Area</option>
                              {areas.map((area, index) => (
                                <option key={index} value={area.id}>
                                  {area.area}
                                </option>
                              ))}
                            </select>
                          </td>



                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination Controls */}
                  <div className="mt-3 d-flex justify-content-center">
                    <nav>
                      <ul className="pagination">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                            ‹
                          </button>
                        </li>

                        {currentPage > 2 && (
                          <>
                            <li className="page-item">
                              <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
                            </li>
                            {currentPage > 3 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                          </>
                        )}

                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(
                            (page) =>
                              page === currentPage ||
                              page === currentPage - 1 ||
                              page === currentPage + 1
                          )
                          .map((page) => (
                            <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => handlePageChange(page)}>{page}</button>
                            </li>
                          ))}

                        {currentPage < totalPages - 1 && (
                          <>
                            {currentPage < totalPages - 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
                            <li className="page-item">
                              <button className="page-link" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
                            </li>
                          </>
                        )}

                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                            ›
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default EmpList;
